import { assertIs, assertTruthy, exhaustiveCaseGuard, forceCheckedIndexedAccess, lazyRef, noAvailableOptions, sortByAccentAwareCaseInsenstive, sortByDayJS, sortByMany, UiOptions, useWatchLater } from "src/helpers/utils";
import { computed, defineComponent, onMounted, ref } from "vue";
import { Guid } from "src/interfaces/InleagueApiV1";
import { FormKit } from "@formkit/vue";
import { listGames, listPractices, listAbsenteeCandidates, Practice, Game, updateAbsences, UpdateAbsenceRequest, getMenu, AbsenteeCandidate } from "./AttendanceTracker.io"
import { axiosAuthBackgroundInstance, axiosInstance } from "src/boot/AxiosInstances";
import { ReactiveReifiedPromise } from "src/helpers/ReifiedPromise";
import { AbsenceForm, AbsenceListingElement, DeleteAbsenceModal, FormAugmentedPlayer, freshAbsenceForm } from "./AttendanceTracker.02";
import dayjs from "dayjs";
import { SoccerBall } from "../SVGs";
import { AxiosErrorWrapper } from "src/boot/AxiosErrorWrapper";
import { GlobalInteractionBlockingRequestsInFlight } from "src/store/EventuallyPinia";
import iziToast from "izitoast";
import { TeamSelectionButtons, useTeamSelectionButtonsPaneSizeWatcher } from "../UserInterface/TeamChooser/TeamChooserSelectButtons";
import { BasicTeamChooserSelectionManager, getTeamChooserMenuNode, teamChooserSelection } from "../UserInterface/TeamChooser/TeamChooserUtils";
import { TeamChooserMenu } from "src/composables/InleagueApiV1.TeamChooser2";
import { AutoModal, DefaultModalController_r, DefaultTinySoccerballBusyOverlay, ModalUtil, useDefaultNoCloseModalIfBusy } from "../UserInterface/Modal";

export default defineComponent({
  setup() {
    const menu = lazyRef<TeamChooserMenu>()
    const menuSelectionManager = lazyRef<BasicTeamChooserSelectionManager>()
    const menuSelection = ref(teamChooserSelection())
    const {openAbsolutePosPanes, rootRef} = useTeamSelectionButtonsPaneSizeWatcher()

    const selectedSeasonUID = computed(() => menuSelectionManager.value.selectedSeasonUID.value || "")
    const selectedTeamID = computed(() => menuSelectionManager.value.selectedTeamIDs.value?.[0] || "")
    const selectedTeam = computed(() => {
      const seasonUID = menuSelectionManager.value.selectedSeasonUID.value
      const competitionUID = menuSelectionManager.value.selectedCompetitionUID.value
      const divID = menuSelectionManager.value.selectedDivID.value
      const teamID = menuSelectionManager.value.selectedTeamIDs.value?.[0]
      if (!seasonUID || !competitionUID || !divID || !teamID) {
        return null
      }

      return getTeamChooserMenuNode(menu.value, "team", {seasonUID, competitionUID, divID, teamID})
    })

    const ready = ref(false)

    type AttendableType = "game" | "practice" | "loose"
    const attendables = (() => {
      const r = ReactiveReifiedPromise<{games: Game[], practices: Practice[]}>(undefined, {defaultDebounce_ms: 250, strategy: "reject-stale"})
      const focusedAttendableType = ref<AttendableType>("game")
      const selectedAttendableID = ref("")
      const selectedAttendableText = computed(() => {
        return options.value.disabled ? "" : options.value.options.find(v => v.value === selectedAttendableID.value)?.label || ""
      })

      const options = (() => {
        return computed(() => {
          if (focusedAttendableType.value === "game") {
            return gameOptions()
          }
          else if (focusedAttendableType.value === "practice") {
            return practiceOptions()
          }
          else if (focusedAttendableType.value === "loose") {
            return noAvailableOptions() // note that this won't be exposed to the user anyway
          }
          else {
            exhaustiveCaseGuard(focusedAttendableType.value)
          }
        })

        function gameOptions() : UiOptions {
          switch (r.underlying.status) {
            case "idle": return noAvailableOptions()
            case "error": return noAvailableOptions("Loading...")
            case "pending": return noAvailableOptions("Loading...")
            case "resolved": {
              return r.underlying.data.games.length === 0
                ? noAvailableOptions()
                : {
                  disabled: false,
                  options: r.underlying.data.games.map(game => {
                    return {label: `${game.fieldAbbrev} - ${dayjs(game.gameStart).format("ddd, MMM/DD/YY h:mm a")}, ${matchupLabel(game)}`, value: game.gameID}
                  })}
            }
          }

          function matchupLabel(game: Game) {
            const home = game.homeTeam?.teamDesignation || "TBD"
            const visitor = game.visitorTeam?.teamDesignation || "TBD"
            return `${home} v. ${visitor}`
          }
        }

        function practiceOptions() : UiOptions {
          switch (r.underlying.status) {
            case "idle": return noAvailableOptions()
            case "error": return noAvailableOptions("Loading...")
            case "pending": return noAvailableOptions("Loading...")
            case "resolved": {
              return r.underlying.data.practices.length === 0
                ? noAvailableOptions()
                : {
                  disabled: false,
                  options: r
                    .underlying
                    .data
                    .practices
                    .map(p => {
                      return {
                        label: `${p.practiceSlot.field.fieldAbbrev} - ${dayjs(p.practiceSlot.start).format("ddd, MMM/DD/YY h:mm a")}`,
                        value: p.practiceSlotAssignmentID.toString()
                      }
                    })
                }
            }
          }
        }
      })()

      const tryLoadFromContext = async () : Promise<void> => {
        const seasonUID = menuSelectionManager.value.selectedSeasonUID.value
        const teamID = menuSelectionManager.value.selectedTeamIDs.value?.[0] || ""

        absenceInfo.reset()

        if (!seasonUID || !teamID) {
          r.reset()
          return;
        }

        await r.run(async () => {
          const games = await listGames(axiosAuthBackgroundInstance, {seasonUID, teamID}).then(vs => {
            return vs.sort(sortByMany(
              sortByAccentAwareCaseInsenstive(v => v.fieldAbbrev),
              sortByDayJS(v => v.gameStart)
            ))
          })

          const practices = await listPractices(axiosAuthBackgroundInstance, {seasonUID, teamID}).then(vs => {
            return vs.sort(sortByMany(
              sortByAccentAwareCaseInsenstive(v => v.practiceSlot.field.fieldAbbrev),
              sortByDayJS(v => v.practiceSlot.start)
            ))
          })

          return {games, practices}
        }).getResolvedOrFail()

        if (!options.value.options.find(v => v.value === selectedAttendableID.value)) {
          selectedAttendableID.value = forceCheckedIndexedAccess(options.value.options, 0)?.value || ""
        }
      }

      return {
        tryLoadFromContext,
        get value() { return r.underlying },
        get options() { return options.value },
        get focusedAttendableType() { return focusedAttendableType.value },
        set focusedAttendableType(v) {
          focusedAttendableType.value = v
          // changing the focused type changes our options list, so update our selected option
          selectedAttendableID.value = forceCheckedIndexedAccess(options.value.options, 0)?.value || ""
        },
        selectedAttendableID,
        get selectedAttendableText() {
          return selectedAttendableText.value
        }
      }
    })()

    const absenceInfo = (() => {
      const r = ReactiveReifiedPromise<FormAugmentedPlayer[]>(undefined, {strategy: "reject-stale", defaultDebounce_ms: 250})

      const get = async (args: {seasonUID: Guid, teamID: Guid, type: AttendableType, attendableID: string | number}) => {
        const {seasonUID, teamID, type, attendableID} = args
        return listAbsenteeCandidates(axiosAuthBackgroundInstance, {seasonUID, teamID, attendableType: type, attendableID}).then(vs => {
          return vs.map(v => {
            return {
              absenceForms: type === "game" || type === "practice"
                ? (v.absences.length === 0 ? [freshAbsenceForm()] : [freshAbsenceForm(v.absences[0])])
                : v.absences.map(freshAbsenceForm),
              player: v
            }
          })
        })
      }

      /**
       * `tryLoadFromContext` does the right thing and discards old pending requests,
       * but "silentRefresh" needs to be run to completion before starting another,
       * which has to be managed by the caller. If the caller violates this,
       * we throw an error.
       */
      let isSilentlyRefreshing = false

      const tryGetValidContext = () => {
        const seasonUID = menuSelectionManager.value.selectedSeasonUID.value
        const teamID = menuSelectionManager.value.selectedTeamIDs.value?.[0] ?? ""
        const attendableID = attendables.selectedAttendableID.value
        const type = attendables.focusedAttendableType

        if (!seasonUID || !teamID) {
          return null
        }

        if (type === "game" || type === "practice") {
          if (attendableID === "") {
            return null
          }
        }
        else if (type === "loose") {
          assertTruthy(attendableID === "")
        }
        else {
          exhaustiveCaseGuard(type)
        }

        return {seasonUID, teamID, attendableID, type}
      }

      const tryLoadFromContext = async () : Promise<void> => {
        assertTruthy(!isSilentlyRefreshing)

        const context = tryGetValidContext()

        if (!context) {
          r.reset()
          return;
        }

        await r.run(async () => {
          return await get(context)
        }).getResolvedOrFail()
      }

      /**
       * resolve the data outside of the reified promise (basically, don't mark it "loading" while we load it, caller then becomes responsible for that)
       */
      const silentRefresh = async () : Promise<void> => {
        assertTruthy(!isSilentlyRefreshing)

        const context = tryGetValidContext()

        if (!context) {
          r.reset()
          return;
        }

        try {
          isSilentlyRefreshing = true
          const data = await get(context)
          r.forceResolve(data)
        }
        catch (err) {
          r.forceReject(err)
        }
        finally {
          isSilentlyRefreshing = false
        }
      }

      return {
        reset: () => r.reset(),
        get value() { return r.underlying },
        tryLoadFromContext,
        silentRefresh,
      }
    })()

    const doSaveChanges = async (args: {player: AbsenteeCandidate, form: AbsenceForm}[]) : Promise<void> => {
      const submittable : UpdateAbsenceRequest = {
        absentFrom: {
          seasonUID: selectedSeasonUID.value,
          teamID: selectedTeamID.value,
          type: attendables.focusedAttendableType,
          id: attendables.selectedAttendableID.value
        },
        absences: args
          .map(v => {
            return {
              whoType: v.player.type,
              whoID: v.player.id,
              absenceID: attendables.focusedAttendableType === "game" || attendables.focusedAttendableType === "practice"
                ? "" // game/practice is keyed on (who, what) (e.g. (player, game) or etc.)
                : attendables.focusedAttendableType === "loose"
                ? v.form.origin?.absenceID || "" // loose things are keyed on absenceID (for ones that exist, if we're creating one we won't have one)
                : exhaustiveCaseGuard(attendables.focusedAttendableType),
              wasAbsent: v.form.data.absent,
              comments: v.form.data.comments,
              datetime: v.form.data.datetime,
            }
          })
      }

      GlobalInteractionBlockingRequestsInFlight.withSpinner(async () => {
        try {
          await updateAbsences(axiosInstance, submittable)
          await absenceInfo.silentRefresh()
          iziToast.success({message: "Absence info updated."})
        }
        catch (err) {
          AxiosErrorWrapper.rethrowIfNotAxiosError(err)
        }
      })
    }

    const deleteOneModalController = (() => {
      const {busy, onCloseCB} = useDefaultNoCloseModalIfBusy()

      const doSubmit = async (args: {player: AbsenteeCandidate, form: AbsenceForm}) => {
        try {
          try {
            busy.value = true
            await doSaveChanges([{
              player: args.player,
              form: {
                ...args.form,
                data: {
                  ...args.form.data,
                  absent: false
                }
              }
            }])
          }
          finally {
            busy.value = false
          }
          deleteOneModalController.close()
        }
        catch (err) {
          AxiosErrorWrapper.rethrowIfNotAxiosError(err)
        }
      }

      return DefaultModalController_r<{player: AbsenteeCandidate, form: AbsenceForm}>({
        title: ModalUtil.simpleTitle("Confirm Delete Absence"),
        content: data => {
          if (!data) {
            return null
          }

          return <div>
            <DeleteAbsenceModal
              player={data.player}
              form={data.form}
              onSubmit={() => doSubmit(data)}
              onCancel={() => {
                deleteOneModalController.close()
              }}
            />
            <DefaultTinySoccerballBusyOverlay if={busy.value}/>
          </div>
        }
      }, {onCloseCB})
    })()

    const menuChangeListener = useWatchLater(() => [selectedSeasonUID.value, selectedTeamID.value], async () => {
      await attendables.tryLoadFromContext()
      await absenceInfo.tryLoadFromContext()
    })

    onMounted(async () => {
      GlobalInteractionBlockingRequestsInFlight.withSpinner(async () => {
        menu.value = await getMenu(axiosAuthBackgroundInstance)
        menuSelectionManager.value = BasicTeamChooserSelectionManager({
          mut_selection: menuSelection,
          menu: menu.value,
        })

        menuSelectionManager.value.selectedTeamIDs

        menuChangeListener.start()

        ready.value = true

        await attendables.tryLoadFromContext()
        await absenceInfo.tryLoadFromContext()
      })
    })

    return () => {
      if (!ready.value) {
        return null
      }

      return <div style="--fk-margin-outer:none; --fk-padding-input: .35em; --fk-bg-input:white;" ref={rootRef}>
        <AutoModal controller={deleteOneModalController} data-test="DeleteOneModal"/>
        <h2>Absence Tracker</h2>

        <TeamSelectionButtons
          teamSelectMode="single"
          menu={menu.value}
          selectionManager={menuSelectionManager.value}
          levels={{
            season: true,
            competition: true,
            division: true,
            team: true,
          }}
          mut_openAbsolutePosPanes={openAbsolutePosPanes}
          data-test="teamChooserButtons"
          data-test-seasonUID={selectedSeasonUID.value}
          data-test-teamID={selectedTeamID.value}
        />
        {selectedSeasonUID.value && selectedTeamID.value
          ? <div>
            {attendables.value.status === "idle"
              ? null
              : <div>
                <div class="flex items-center gap-2 my-2">
                  <label class="flex items-center gap-1">
                    <input
                      type="radio"
                      name="attendance-tracker/attendable-type"
                      checked={attendables.focusedAttendableType === "game"}
                      onInput={() => {
                        attendables.focusedAttendableType = "game"
                        absenceInfo.tryLoadFromContext()
                      }}
                      data-test="game"
                    />
                    <span>Games</span>
                  </label>
                  <label class="flex items-center gap-1">
                    <input
                      type="radio"
                      name="attendance-tracker/attendable-type"
                      checked={attendables.focusedAttendableType === "practice"}
                      onInput={() => {
                        attendables.focusedAttendableType = "practice"
                        absenceInfo.tryLoadFromContext()
                      }}
                      data-test="practice"
                    />
                    <span>Practices</span>
                  </label>
                  <label class="flex items-center gap-1">
                    <input
                      type="radio"
                      name="attendance-tracker/attendable-type"
                      checked={attendables.focusedAttendableType === "loose"}
                      onInput={() => {
                        attendables.focusedAttendableType = "loose"
                        absenceInfo.tryLoadFromContext()
                      }}
                      data-test="loose"
                    />
                    <span>Other</span>
                  </label>
                </div>
                {attendables.focusedAttendableType === "game" || attendables.focusedAttendableType === "practice"
                  ? <FormKit
                    type="select"
                    disabled={attendables.options.disabled}
                    options={attendables.options.options}
                    data-test={attendables.focusedAttendableType === "game" ? "gameID"
                      : attendables.focusedAttendableType === "practice" ? "practiceSlotAssignmentID"
                      : exhaustiveCaseGuard(attendables.focusedAttendableType)
                    }
                    v-model={attendables.selectedAttendableID.value}
                    onInput={(v:any) => {
                      if (attendables.selectedAttendableID.value === v) {
                        return
                      }
                      attendables.selectedAttendableID.value = v
                      absenceInfo.tryLoadFromContext()
                    }}
                  />
                  : null}
              </div>
            }
            <div class="bg-white border rounded-md my-2">
              <div class="bg-green-800 text-white px-1 rounded-t-md">
                <span>Players</span>
                <span>{selectedTeam.value ? ` - ${selectedTeam.value.name}` : ""}</span>
                <div>{attendables.selectedAttendableText}</div>
              </div>
              <div class="px-2">
                {absenceInfo.value.status === "idle"
                  ? null
                  : absenceInfo.value.status === "pending"
                  ? <div class="p-2 flex gap-2 items-center"><SoccerBall/>Loading...</div>
                  : absenceInfo.value.status === "error"
                  ? <div>Sorry, something went wrong loading players</div>
                  : absenceInfo.value.status === "resolved"
                  ? <AbsenceListingElement
                    data-test={`absenceListing/${attendables.focusedAttendableType}/${attendables.selectedAttendableID.value}`}
                    listing={absenceInfo.value.data}
                    mode={attendables.focusedAttendableType}
                    onSaveGameOrPracticeListing={() => {
                      assertTruthy(attendables.focusedAttendableType === "game" || attendables.focusedAttendableType === "practice")
                      assertIs(absenceInfo.value.status, "resolved")

                      doSaveChanges(
                        absenceInfo.value.data.map((v) => {
                          assertTruthy(v.absenceForms.length === 1)
                          return {player: v.player, form: v.absenceForms[0]}
                        })
                      )
                    }}
                    onSaveSingleLooseAbsence={({player, form}) => {
                      doSaveChanges([{player, form}])
                    }}
                    onDeleteSingleLooseAbsence={(args) => {
                      deleteOneModalController.open(args)
                    }}
                  />
                  : exhaustiveCaseGuard(absenceInfo.value)
                }
              </div>
            </div>
          </div>
          : null}
      </div>
    }
  }
})
